{ Jadelet, Modal, Observable } from @danielx/dui

{ readPracticeMetadata, writePracticeMetadata, practiceChannels, type PracticePart } from ../lib/midi/practice-metadata.civet
{ buildNoteIndex, indexNoteIds } from ../lib/midi/note-index.civet
{ measureCount as countMeasures } from ../lib/midi/measures.civet
{ noteName } from ../lib/util.civet

{max, min} := Math

// The practice setup editor: pick the channels you play, optionally split a
// single channel into RH/LH hands at a pitch (note-level parts, the same ids
// a Synthesia import produces), mark practice sections by measure, and rate
// the difficulty. Opened by the mode modal's "Song Practice Setup…" and by
// entering practice/game on a song without metadata; running practice
// assumes whatever metadata exists is complete. Fingering annotation is
// still import-only (future canvas work).
Template := Jadelet.exec """
  section.practice-setup
    h2 Practice Setup
    button.close(click=@close) X
    p Choose the part you'll play — everything else becomes the backing track.
    table
      thead
        tr
          th Play
          th Channel
          th Instrument
          th Notes
      tbody
        @items
    label.split(class=@splitHiddenClass title="Write the selection as two note-level hand parts split at this pitch")
      input(type="checkbox" checked=@splitHands)
      span Split into hands at
      select(value=@splitPitch)
        @splitOptions
    h3 Sections
    p.sections-blurb Optional practice sections by measure, for section drills and the lesson plans to come.
    table.sections
      thead
        tr
          th Name
          th Start
          th End
          th
      tbody
        @sectionItems
    button.flat(click=@addSection) + Add Section
    label.difficulty
      span Difficulty
      input(type="number" min="0" max="100" step="1" value=@difficulty)
      span.scale / 100
    section.save-row
      button(click=@save disabled=@saveDisabled) Save
      button.primary(click=@saveAndStart disabled=@saveDisabled) @saveAndStartLabel
"""

RowTemplate := Jadelet.exec """
tr
  td
    input(type="checkbox" checked=@selected)
  td @name
  td @instrument
  td @noteCount
"""

SectionRowTemplate := Jadelet.exec """
tr
  td
    input(type="text" value=@name placeholder="A")
  td
    input(type="number" min="1" value=@start)
  td
    input(type="number" min="1" value=@end)
  td
    button.flat(click=@remove) ✕
"""

OptionTemplate := Jadelet.exec """
  option(@value) @name
"""

export PracticeSetup := (player: Player) ->
  let rows: {channel: number, selected: any}[]
  selectedCount := Observable 0
  // The lists reflect the song at the moment the dialog opens, not at app
  // construction — showPracticeSetup bumps this to rebuild everything
  shownCount := Observable 0

  splitHands := Observable false
  splitPitch := Observable "60"
  // Existing note-level hand parts (a Synthesia import, or an earlier split
  // at a pitch we can't recover) are preserved verbatim on save unless the
  // user actually touches the split controls — regenerating them as a pitch
  // split at the seeded default would silently reassign crossed-hand notes
  let originalNoteParts: PracticePart[]?
  originalChannelsKey .= ""
  splitDirty .= false
  splitHands.observe -> splitDirty = true
  splitPitch.observe -> splitDirty = true

  type SectionRow = {name: any, start: any, end: any}
  sectionRows .= [] as SectionRow[]
  sectionsChanged := Observable 0

  difficulty := Observable ""

  measureCount := ->
    max 1, countMeasures player.song.timeSignatureMap, player.song.beatLength()

  addSectionRow := (name: string, start: number, end: number) ->
    row := {name: Observable(name), start: Observable(String start), end: Observable(String end)}
    sectionRows.push row
    row

  // Seed the editor from the song's existing metadata each time it opens
  loadFromSong := ->
    meta := readPracticeMetadata player.song
    sectionRows = []
    meta?.sections?.forEach ({name, startMeasure, endMeasure}) ->
      addSectionRow name ?? "", startMeasure, endMeasure
    sectionsChanged.increment()
    difficulty if meta?.difficulty? then String meta.difficulty else ""
    // A single note-level RH/LH pair reads back as a split
    parts := meta?.parts ?? []
    isNoteSplit := parts.length is 2 and parts.every (p) -> p.notes?
    splitHands isNoteSplit
    splitPitch "60"
    originalNoteParts = if isNoteSplit then parts else undefined
    originalChannelsKey = if meta then practiceChannels(meta).slice().sort().join "," else ""
    // the seeding above is not the user touching the controls
    splitDirty = false

  self :=
    close: -> Modal.hide()

    items: ->
      shownCount()
      song := player.song
      player.synthUpdated()
      song.instrumentMapChanged()

      {banks} := player.synth()
      {channelBanks, instrumentMap} := song

      noteCounts := Array(16).fill 0
      song.events.forEach (e) ->
        noteCounts[e.c]++ if e.type is "noteOn"

      // Editing an existing setup starts from its part
      meta := readPracticeMetadata song
      existing := new Set if meta then practiceChannels meta else []

      rows = []
      // no reactive reads here: row.selected() inside this binding would make
      // every checkbox a dependency and rebuild (and reset) the rows on click
      initialCount .= 0

      rendered := [0...16].filter (c) ->
        noteCounts[c] > 0
      .map (c) ->
        sel := existing.has c
        initialCount++ if sel
        selected := Observable sel
        selected.observe ->
          selectedCount rows.filter((row) -> row.selected()).length
        rows.push {channel: c, selected}

        program := instrumentMap.get(c) ?? 0

        RowTemplate
          selected: selected
          name: `Ch.${c + 1}`
          instrument: banks?[channelBanks[c]]?[program]?.name ?? `Program ${program + 1}`
          noteCount: noteCounts[c]

      selectedCount initialCount
      rendered

    // The hand split applies to exactly one channel: with several checked,
    // hands can't be told apart within them, so the control hides
    splitHiddenClass: ->
      "hidden" unless selectedCount() is 1

    splitHands: splitHands
    splitPitch: splitPitch
    splitOptions: ->
      // C2..C6, every semitone; middle C default
      [36..84].map (n) ->
        OptionTemplate
          name: noteName n
          value: String n

    sectionItems: ->
      sectionsChanged()
      sectionRows.map (row) ->
        SectionRowTemplate
          name: row.name
          start: row.start
          end: row.end
          remove: ->
            sectionRows.splice sectionRows.indexOf(row), 1
            sectionsChanged.increment()

    addSection: ->
      last := sectionRows[sectionRows.length - 1]
      start := if last then min(parseInt(last.end(), 10) + 1, measureCount()) else 1
      addSectionRow "", start, min(start + 7, measureCount())
      sectionsChanged.increment()

    difficulty: difficulty

    saveDisabled: ->
      selectedCount() is 0

    saveAndStartLabel: ->
      if player.practiceSetupTarget is "game" then "Save & Play 🎮" else "Save & Practice 🎯"

    save: -> self.write() and Modal.hide()
    saveAndStart: ->
      if self.write()
        Modal.hide()
        // whichever mode's button opened the dialog
        player.startPractice player.practiceSetupTarget

    // Write the editor's state into the song's metadata; false if invalid
    write: ->
      channels := rows.filter (row) -> row.selected()
      .map (row) -> row.channel
      return false unless channels.length

      song := player.song

      parts: PracticePart[] .= [{name: "You", channels}]
      if splitHands() and channels.length is 1
        if originalNoteParts and not splitDirty and channels.join(",") is originalChannelsKey
          // untouched existing hand split: keep it exactly as authored
          parts = originalNoteParts
        else
          c := channels[0]
          split := parseInt splitPitch(), 10
          index := buildNoteIndex song
          ids := indexNoteIds index, song.ticksPerBeat
          rh := [] as string[]
          lh := [] as string[]
          index.forEach (note, i) ->
            return unless note.c is c
            (if note.n >= split then rh else lh).push ids[i]
          parts = [{name: "RH", notes: rh}, {name: "LH", notes: lh}]

      sections := sectionRows.map (row) ->
        m := measureCount()
        start := clampMeasure row.start(), m
        {
          name: row.name().trim() or undefined
          startMeasure: start
          endMeasure: max start, clampMeasure row.end(), m
        }
      .filter (s) -> s.startMeasure >= 1

      d := parseInt difficulty(), 10

      // Keep everything the dialog doesn't edit (fingering, discard, …)
      writePracticeMetadata song, {
        ...readPracticeMetadata song
        version: 2
        parts
        ...if sections.length then {sections} else {sections: undefined}
        ...if 0 <= d <= 100 then {difficulty: d} else {difficulty: undefined}
      }

      player.unsaved true
      player.pushState player.getState()
      true

    element: null as! HTMLElement

  self.element = Template self

  player.showPracticeSetup = ->
    shownCount.increment()
    loadFromSong()
    Modal.show self.element
    // like settings.civet: the select's value only sticks once options exist
    setTimeout ->
      s := self.element.querySelector('label.split select') as HTMLSelectElement?
      s.value = splitPitch() if s

  return self

clampMeasure := (value: string, measures: number) ->
  n := parseInt value, 10
  if Number.isFinite n
    min max(n, 1), measures
  else
    0

export default PracticeSetup
